import { Component, Element, Host, Listen, Prop, State, Watch, h } from '@stencil/core';
import { type UIAppConfig } from '@sydle/ui-app';
import { Consumer, getConfig } from '@sydle/ui-utils';
import { refreshHead } from 'packages/sydle-ui-blog/src/utils/utils';
import { searchPosts } from '../../../api/blog-api/search-posts';
import { GetPostsparams as GetPostsParams, type BlogCategory } from '../../../types/blog';

@Component({
  tag: 'sy-blog-search-list',
  styleUrl: './scss/index.scss',
  shadow: true,
})
export class SyBlogSearchList {
  private cardList: HTMLSyBlogCardListElement;
  private consumer: Consumer;
  @Element() el: HTMLSyBlogSearchListElement;

  /**
   *
   */
  @Prop() getPostsParams?: GetPostsParams;

  /**
   * Defines if infinite scroll is enabled
   */
  @Prop() infiniteScroll = false;

  /**
   * Defines the number of posts per page
   */
  @Prop() postsPerPage: number;

  /**
   * Search placeholder
   */
  @Prop() searchPlaceholder: string;

  /**
   * Text of the all categories label
   */
  @Prop() allCategoriesLabel: string;

  /**
   * Text of the back to top button
   */
  @Prop() backToTopLabel: string;

  /**
   * Text of the content request button
   */
  @Prop() contentRequestLabel: string;

  /**
   * Text of the empty list message
   */
  @Prop() emptyListMessage: string;

  @State() query = '';

  @State() categoryId = '';

  @State() categories: BlogCategory[] = [];

  @State() categoriesPromise: Promise<BlogCategory[]>;

  private loadCategories(): void {
    if (this.categoriesPromise === undefined) {
      this.categoriesPromise = window.blogFacade?.getCategories({ size: 100 }).then((response) => {
        if (response.error) {
          console.error(response.error);
        }
        this.categories = response.data as BlogCategory[];
        return response.data as BlogCategory[];
      });
    }
  }

  connectedCallback() {
    this.consumer = new Consumer(this.el);
    this.consumer.subscribe('getPostsParams', (postsParams: any) => (this.getPostsParams = postsParams));
  }

  disconnectedCallback() {
    this.consumer.disconnect();
  }
  private categoryClickHandler(ev: MouseEvent) {
    this.categoryId = (ev.target as Element).id;
    this.cardList.resetList();
  }

  async componentWillLoad() {
    this.loadCategories();
    const params = new URLSearchParams(window.location.search);
    this.query = params.get('query') ?? '';
    this.categoryId = params.get('categoryId') ?? '';
  }

  @Watch('query')
  updateQuerySearchParam(search: string): void {
    const url = new URL(window.location.href);
    if (search) url.searchParams.set('query', search);
    else url.searchParams.delete('query');
    window.history.pushState({}, '', url);
  }

  @Watch('categoryId')
  updateCategorySearchParam(categoryId: string): void {
    const url = new URL(window.location.href);
    if (categoryId) url.searchParams.set('categoryId', categoryId);
    else url.searchParams.delete('categoryId');
    window.history.pushState({}, '', url);
  }

  @Listen('syInputDidChange')
  inputChangeHandler(ev: CustomEvent<{ value: string }>): void {
    this.query = ev.detail.value ?? '';
    this.cardList.resetList();
  }

  @Listen('syCardListDidAskForMoreContent')
  contentRequestHandler(ev: CustomEvent<{ pageIndex: number; pageSize: number }>): void {
    searchPosts({
      appIdentifier: this.getPostsParams?.appIdentifier || '',
      pageIndex: ev.detail.pageIndex,
      pageSize: ev.detail.pageSize,
      search: this.query,
      params: { categoryId: this.categoryId },
    }).then((response) => {
      this.cardList.posts = this.cardList.posts.concat(response.data?.posts || []);
      this.cardList.total = response.data?.total || 0;

      const route = getConfig<UIAppConfig>('ui-app')?.routes.find((route) => route.name === 'search');
      const titleTemplate = (route?.dynamicTitle ? route?.titleTemplate : route?.title) || '';
      refreshHead(response.data, titleTemplate, '', false);
    });
  }

  render() {
    return (
      <Host>
        <div class="container">
          <sy-input
            type="text"
            value={this.query}
            placeholder={this.searchPlaceholder}
            icon="search"
            iconPosition="left"
          />
          <div id="categories">
            <sy-text
              type="body"
              class={`category ${!this.categoryId ? 'active' : ''}`}
              onClick={(e) => this.categoryClickHandler(e)}
            >
              {this.allCategoriesLabel}
            </sy-text>
            {this.categories.length ? (
              this.categories.map((category) => (
                <sy-text
                  id={category._id}
                  key={category._id}
                  class={`category ${this.categoryId === category._id ? 'active' : ''}`}
                  onClick={(e) => this.categoryClickHandler(e)}
                  type="body"
                >
                  {category.name}
                </sy-text>
              ))
            ) : (
              <sy-blog-skeleton-load style={{ width: '16rem' }} />
            )}
          </div>
          <sy-blog-card-list
            backToTopLabel={this.backToTopLabel}
            contentRequestLabel={this.contentRequestLabel}
            pageSize={this.postsPerPage}
            ref={(el: HTMLSyBlogCardListElement) => (this.cardList = el)}
            infiniteScroll={this.infiniteScroll}
            emptyListMessage={this.emptyListMessage}
          />
        </div>
      </Host>
    );
  }
}
